import { find, isNil, some, filter, uniq } from "lodash";
import { EventBus } from "src/bus/eventBus";
import { Services } from "src/services/Services";

export default {
    methods: {
        /**
         *
         * @param {*} convention
         * @param {*} hasSubscribed
         */
        async selectConvention(convention) {
            if (this.canAddConvention(convention)) {
                const updatedUser = await Services.api.user.selectConvention(
                    convention.conventionId
                );
                const selectedConventionsPopulated = await Services.api.user.getMyConventions();

                if (updatedUser) {
                    EventBus.$emit("user:updated", {
                        ...updatedUser,
                        selectedConventionsPopulated,
                    });
                }
            }
        },

        /**
         *
         * @param {*} convention
         */
        async removeConvention(convention) {
            if (this.canViewAllConventions) {
                const updatedUser = await Services.api.user.removeConvention(
                    convention.conventionId
                );
                const selectedConventionsPopulated = await Services.api.user.getMyConventions();

                if (updatedUser) {
                    EventBus.$emit("user:updated", {
                        ...updatedUser,
                        selectedConventionsPopulated,
                    });
                }
            }
        },

        /**
         *
         * @param {*} convention
         * @param {*} hasSubscribed
         */
        async updateConventionSubscription(convention, hasSubscribed = false) {
            if (this.hasConvention(convention)) {
                const updatedUser = await Services.api.user.updateConventionSubscription({
                    number: convention.conventionId,
                    push: hasSubscribed,
                });
                const selectedConventionsPopulated = await Services.api.user.getMyConventions();

                if (updatedUser) {
                    EventBus.$emit("user:updated", {
                        ...updatedUser,
                        selectedConventionsPopulated,
                    });
                }
            }
        },

        /**
         *
         */
        async updateInfo(newUser) {
            const updatedUser = await Services.api.user.updateInfo(newUser);
            const selectedConventionsPopulated = await Services.api.user.getMyConventions();

            if (updatedUser) {
                EventBus.$emit("user:updated", {
                    ...updatedUser,
                    selectedConventionsPopulated,
                });
            }
        },

        async updateNotifications(notifications) {
            const updatedUser = await Services.api.user.updateNotifications(notifications);
            const selectedConventionsPopulated = await Services.api.user.getMyConventions();

            if (updatedUser) {
                EventBus.$emit("user:updated", {
                    ...updatedUser,
                    selectedConventionsPopulated,
                });
            }
        },

        /**
         *
         */
        async updatePassword(passwordRequest) {
            return await Services.api.user.updatePassword(passwordRequest);
        },

        /**
         *
         * @param convention
         */
        canAddConvention(convention) {
            const hasConvention = this.hasConvention(convention);
            return this.canViewAllConventions
                ? !hasConvention
                : !hasConvention && this.conventionLeftNumber;
        },

        /**
         *
         * @param convention
         */
        hasConvention(convention) {
            return some(
                this.user.selectedConventionsPopulated,
                c =>
                    c.conventionId === convention.conventionId ||
                    c.conventionId === convention.parentConventionId
            );
        },

        /**
         *
         * @param convention
         */
        hasConventionFamily(convention) {
            const conventionIdsInSameFamily = this.getConventionIdsInSameFamily(convention);
            return some(this.user.selectedConventionsPopulated, convention =>
                conventionIdsInSameFamily.some(
                    conventionId => conventionId === convention.conventionId
                )
            );
        },

        /**
         *
         * @param convention
         */
        getConventionIdsInSameFamily(convention) {
            const parentConventionId = convention.parentConventionId;
            if (!isNil(convention.parentConvention)) {
                const siblingsConventionIds = convention.parentConvention.childrenConventions.map(
                    c => c.conventionId
                );
                return uniq([
                    ...siblingsConventionIds,
                    parentConventionId,
                    convention.conventionId,
                ]);
            } else {
                const childrenConventionIds = (convention.childrenConventions || []).map(
                    c => c.conventionId
                );
                return uniq([...childrenConventionIds, convention.conventionId]);
            }
        },

        /**
         *
         * @param user
         * @param convention
         */
        hasSubscribed(convention) {
            return some(
                this.user.selectedConventions,
                c =>
                    c.push &&
                    (c.number === convention.conventionId ||
                        c.number === convention.parentConventionId)
            );
        },

        /**
         *
         */
        getAllowedConventionsNumber() {
            // TODO Change name
            if (this.isAdmin()) {
                return null;
            }
            const diodeService = this.getSubscriptionService("consultation-base-diode");
            return diodeService ? diodeService.quantity : null;
        },

        /**
         *
         */
        getAllowedSubscriptionNumber() {
            if (this.isAdmin()) {
                return null;
            }
            const diodeService = this.getSubscriptionService("alerte-conventionnelle");
            return diodeService ? diodeService.quantity : null;
        },

        isSubscribedToInfoMaladie() {
            const service = this.getSubscriptionService("alerte-maladie-prevoyance");
            return !isNil(service);
        },

        getSubscriptionService(serviceName) {
            if (!this.user.subscription) return null;
            return find(this.user.subscription.services, s => s.name == serviceName);
        },

        /**
         *
         */
        isAdmin() {
            return this.user.permission === "adm_sys";
        },
    },
    computed: {
        canViewAllConventions() {
            if (this.isAdmin()) {
                return true;
            }
            const number = this.getAllowedConventionsNumber();
            return number === 0;
        },

        canSubscribe() {
            if (this.isAdmin()) {
                return true;
            }
            const number = this.getAllowedSubscriptionNumber();
            return number !== null;
        },

        canSubscribeConvention() {
            if (this.isAdmin()) {
                return true;
            }
            const number = this.getAllowedSubscriptionNumber();
            if (number === null) return false;
            if (number === 0) return true;
            const conventions = filter(this.user.selectedConventions, c => c.push);
            return conventions.length < number;
        },

        conventionLeftNumber() {
            if (!this.user) return;
            const conventions = this.user.selectedConventionsPopulated;
            const myConventionsNumber = conventions && conventions.length ? conventions.length : 0;
            const allowConventionsNumber = this.getAllowedConventionsNumber();
            if (this.canViewAllConventions) return null;
            return allowConventionsNumber - myConventionsNumber;
        },

        isSubscribedToCompactInfo() {
            const service = this.getSubscriptionService("info-compacte");
            return !isNil(service) || this.isAdmin();
        },

        isSubscribedToPanorama() {
            const service = this.getSubscriptionService("panorama");
            return !isNil(service) || this.isAdmin();
        },
    },
};
